import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Building2, Plus, Lock } from 'lucide-react';
import './CompanyAutocomplete.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface CompanySuggestion {
  id: number;
  name: string;
  company_code?: string;
  city?: string;
  activity_sector?: string;
  taken?: boolean;
}

interface CompanyAutocompleteProps {
  value: string;
  onChange: (name: string) => void;
}

const CompanyAutocomplete: React.FC<CompanyAutocompleteProps> = ({ value, onChange }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState<CompanySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(!!value);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync external value → internal query when it changes from outside
  useEffect(() => {
    setQuery(value || '');
    setSelected(!!value);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        // If user typed but didn't select, keep the typed text as the value
        if (query.trim() && query !== value) {
          onChange(query.trim());
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [query, value, onChange]);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/companies/autocomplete-for-job?q=${encodeURIComponent(q)}&limit=10`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.success) setSuggestions(data.data || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    setSelected(false);
    setOpen(true);
    onChange(q); // keep parent in sync as user types
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 300);
  };

  const handleSelect = (s: CompanySuggestion) => {
    if (s.taken) return;
    setQuery(s.name);
    setSelected(true);
    onChange(s.name);
    setOpen(false);
    setSuggestions([]);
  };

  const handleClear = () => {
    setQuery('');
    setSelected(false);
    onChange('');
    setSuggestions([]);
    setOpen(false);
  };

  const handleFocus = () => {
    if (query.length >= 2) {
      setOpen(true);
      fetchSuggestions(query);
    }
  };

  // "Create new" option — just set the typed text
  const useTyped = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSelected(true);
    onChange(trimmed);
    setOpen(false);
    setSuggestions([]);
  };

  const showCreateNew = query.trim().length >= 2 && !suggestions.some(s => s.name.toLowerCase() === query.toLowerCase());

  return (
    <div className="company-autocomplete" ref={wrapperRef}>
      <div className={`ca-input-wrap ${selected ? 'selected' : ''} ${open ? 'open' : ''}`}>
        <Search size={15} className="ca-icon" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={handleFocus}
          placeholder="Ieškoti įmonės pavadinimo..."
          autoComplete="off"
        />
        {loading && <span className="ca-spinner" />}
        {query && !loading && (
          <button className="ca-clear" onClick={handleClear} title="Išvalyti" type="button">
            <X size={14} />
          </button>
        )}
      </div>

      {open && (suggestions.length > 0 || showCreateNew) && (
        <div className="ca-dropdown">
          {suggestions.map(s => (
            <div
              key={s.id}
              className={`ca-item ${s.taken ? 'taken' : ''}`}
              onClick={() => handleSelect(s)}
              title={s.taken ? 'Ši įmonė jau susieta su kitu darbdaviu' : undefined}
            >
              <div className="ca-item-icon">
                {s.taken ? <Lock size={14} /> : <Building2 size={14} />}
              </div>
              <div className="ca-item-info">
                <span className="ca-item-name">{s.name}</span>
                {(s.city || s.activity_sector) && (
                  <span className="ca-item-meta">
                    {[s.city, s.activity_sector].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
              {s.taken && <span className="ca-taken-badge">Užimta</span>}
            </div>
          ))}

          {showCreateNew && (
            <div className="ca-item create-new" onClick={useTyped}>
              <div className="ca-item-icon"><Plus size={14} /></div>
              <div className="ca-item-info">
                <span className="ca-item-name">Naudoti: „{query.trim()}"</span>
                <span className="ca-item-meta">Sukurti naują pavadinimą</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyAutocomplete;
